import { Component, Input, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MaintenanceRecord,
  MaintenanceType,
  MaintenanceStatus,
  CreateMaintenanceRecordDto,
  UpdateMaintenanceRecordDto
} from '../../../core/models/drone.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-maintenance-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './maintenance-modal.component.html',
  styleUrls: ['./maintenance-modal.component.scss']
})
export class MaintenanceModalComponent {
  private fb = inject(FormBuilder);

  @Input() droneId: string = '';
  @Input() droneName: string = '';

  @Output() saved = new EventEmitter<CreateMaintenanceRecordDto | { id: string; dto: UpdateMaintenanceRecordDto }>();
  @Output() cancelled = new EventEmitter<void>();

  // State
  isOpen = signal(false);
  isProcessing = signal(false);
  isEditMode = signal(false);
  editingRecordId = signal<string | null>(null);

  // Form
  form!: FormGroup;

  // Options for dropdowns
  readonly typeOptions: { value: MaintenanceType; label: string }[] = [
    { value: 'scheduled', label: 'Scheduled Maintenance' },
    { value: 'repair', label: 'Repair' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'upgrade', label: 'Upgrade' },
    { value: 'other', label: 'Other' }
  ];

  readonly statusOptions: { value: MaintenanceStatus; label: string }[] = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      type: ['scheduled', [Validators.required]],
      title: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      scheduledDate: ['', [Validators.required]],
      completedDate: [''],
      status: ['scheduled', [Validators.required]],
      performedBy: [''],
      cost: [null],
      notes: ['']
    });
  }

  /**
   * Open modal for creating a new maintenance record
   */
  openCreate(): void {
    this.isEditMode.set(false);
    this.editingRecordId.set(null);
    this.form.reset({
      type: 'scheduled',
      status: 'scheduled',
      scheduledDate: this.getTodayDate()
    });
    this.isOpen.set(true);
  }

  /**
   * Open modal for editing an existing maintenance record
   */
  openEdit(record: MaintenanceRecord): void {
    this.isEditMode.set(true);
    this.editingRecordId.set(record.id);
    this.form.patchValue({
      type: record.type,
      title: record.title,
      description: record.description || '',
      scheduledDate: record.scheduledDate,
      completedDate: record.completedDate || '',
      status: record.status,
      performedBy: record.performedBy || '',
      cost: record.cost || null,
      notes: record.notes || ''
    });
    this.isOpen.set(true);
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isProcessing.set(true);

    const formData = this.form.value;

    if (this.isEditMode() && this.editingRecordId()) {
      // Update existing record
      const dto: UpdateMaintenanceRecordDto = {
        type: formData.type,
        title: formData.title,
        description: formData.description || undefined,
        scheduledDate: formData.scheduledDate,
        completedDate: formData.completedDate || undefined,
        status: formData.status,
        performedBy: formData.performedBy || undefined,
        cost: formData.cost || undefined,
        notes: formData.notes || undefined
      };

      this.saved.emit({ id: this.editingRecordId()!, dto });
    } else {
      // Create new record
      const dto: CreateMaintenanceRecordDto = {
        droneId: this.droneId,
        type: formData.type,
        title: formData.title,
        description: formData.description || undefined,
        scheduledDate: formData.scheduledDate,
        performedBy: formData.performedBy || undefined,
        cost: formData.cost || undefined,
        notes: formData.notes || undefined
      };

      this.saved.emit(dto);
    }

    // Simulate brief processing delay then close
    setTimeout(() => {
      this.isProcessing.set(false);
      this.isOpen.set(false);
    }, 300);
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }

  /**
   * Check if a field is invalid
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  /**
   * Get today's date in YYYY-MM-DD format
   */
  private getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Get modal title
   */
  getModalTitle(): string {
    return this.isEditMode() ? 'Edit Maintenance Record' : 'Add Maintenance Record';
  }
}
